import * as vp from 'vopay';
import React, { createContext, ReactNode, useContext, useReducer } from 'react';

export type GetterSetters<T> = [() => T | undefined, (value: T) => void, () => void];

export type CacheCtx = {
  cache: <T>(path: string, initValue?: T) => GetterSetters<T>;
  clearCache: () => void;
};

export type Action = {
  type: 'storeValue' | 'clearValue' | 'clearCache';
  path: string;
  value?: unknown;
};

export type PathTraverser = (
  pathSegments: string[],
  value: unknown,
  storage: { [key: string]: unknown },
  allowOverwrite: boolean,
) => { [key: string]: unknown };

const CacheContext = createContext<CacheCtx>({
  cache: (_, initValue) => [() => initValue, () => {}, () => {}],
  clearCache: () => {},
});

export const CacheProvider = ({ children }: { children: ReactNode }): React.JSX.Element => {
  const storeValue: PathTraverser = (pathSegments, value, storage, allowOverwrite) => {
    const [seg] = pathSegments;
    const currentStoredItem: unknown = storage[seg] || {};

    if (pathSegments.length === 1) {
      return { [seg]: value };
    }

    if (isStorageObject(currentStoredItem)) {
      return {
        ...storage,
        [seg]: {
          ...currentStoredItem,
          ...storeValue(nextSegment(pathSegments), value, currentStoredItem, allowOverwrite),
        },
      };
    }

    return allowOverwrite
      ? { ...storage, [seg]: storeValue(nextSegment(pathSegments), value, {}, allowOverwrite) }
      : { ...storage, [seg]: currentStoredItem };
  };

  const reducer = (state: { [key: string]: unknown }, action: Action) => {
    const actions = {
      storeValue: () => storeValue(action.path.split('.'), action.value, state, false),
      clearValue: () => storeValue(action.path.split('.'), undefined, state, true),
      clearCache: () => ({}),
    };

    return actions[action.type]();
  };

  const [store, dispatch] = useReducer(reducer, {});

  function cache<T>(path: string, initValue?: T): GetterSetters<T> {
    const pathSegments = path.split('.');

    const getter = (): T => {
      const storedValue = pathSegments.reduce((currentStoredValue, segment, idx, segments) => {
        if (isStorageObject(currentStoredValue)) return currentStoredValue[segment];

        return idx === segments.length - 1 ? currentStoredValue : initValue;
      }, store as unknown);

      return storedValue as T;
    };

    const setter = (value: T) => {
      dispatch({ type: 'storeValue', path, value });
    };

    const reset = () => {
      dispatch({ type: 'clearValue', path });
    };

    return [getter, setter, reset];
  }

  function clearCache() {
    dispatch({ type: 'storeValue', path: '' });
  }

  function isStorageObject(item: unknown): item is { [key: string]: unknown } {
    return vp.isPlainObject(item);
  }

  function nextSegment(pathSegments: string[]) {
    return pathSegments.slice(1, pathSegments.length);
  }

  return <CacheContext.Provider value={{ cache, clearCache }}>{children}</CacheContext.Provider>;
};

export const useCache = (): CacheCtx => useContext(CacheContext);
